<template>
    <teleport to="body" :disabled="!appendToBody">
        <transition name="dialog-fade" @after-enter="afterEnter" @after-leave="afterLeave" @before-leave="beforeLeave">
            <pt-overlay
                v-show="visible"
                :mask="modal"
                :overlay-class="modalClass"
                :z-index="zIndex"
                @click="onModalClick"
            >
                <div
                    ref="dialogRef"
                    v-trap-focus
                    :class="[
                        'pt-dialogv2',
                        {
                            'is-fullscreen': fullscreen,
                            'pt-dialog--center': center,
                        },
                        `pt-dialogv2__${size}`,
                        customClass,
                    ]"
                    aria-modal="true"
                    role="dialog"
                    :aria-label="title || 'dialog'"
                    :style="style"
                    @click.stop=""
                >
                    <div class="pt-dialogv2__header">
                        <slot name="title">
                            <div class="pt-dialogv2__title">
                                <pt-icon
                                    v-if="iconKey"
                                    :icon="iconKey"
                                    :style="{
                                        width: '32px',
                                        height: '32px',
                                    }"
                                    class="pt-dialogv2__title__icon"
                                ></pt-icon>
                                <span>{{ title }}</span>
                            </div>
                            <div
                                v-if="subtitle"
                                :style="{
                                    marginTop: iconKey ? '8px' : '4px',
                                }"
                                class="pt-dialogv2__subtitle"
                            >
                                {{ subtitle }}
                            </div>
                        </slot>
                        <button
                            v-if="showClose"
                            aria-label="close"
                            class="pt-dialogv2__headerbtn"
                            type="button"
                            @click="handleClose"
                        >
                            <pt-icon icon="pt-icon-clear" class="pt-dialog__close pt-icon pt-icon-close"></pt-icon>
                        </button>
                    </div>
                    <template v-if="rendered">
                        <div class="pt-dialogv2__body">
                            <template v-if="hasScroll">
                                <!-- 内置滚动条默认关闭 -->
                                <pt-scrollbar class="pt-dialog__body__scroll" rightOffset="16px">
                                    <slot></slot>
                                </pt-scrollbar>
                            </template>
                            <template v-else>
                                <slot></slot>
                            </template>
                        </div>
                    </template>
                    <div
                        class="pt-dialogv2__footer"
                        :style="{
                            paddingBottom: `${hasFooter ? 40 : 16}px`,
                        }"
                    >
                        <!-- 默认两个按钮，可以控制按钮是否隐藏 -->
                        <template v-if="hasFooter">
                            <slot name="footer">
                                <div class="pt-dialogv2__footer__height">
                                    <pt-button
                                        v-show="showCancelButton"
                                        v-bind="cancelButtonInfo"
                                        :type="cancelButtonInfo?.type || 'outline'"
                                        :color="cancelButtonInfo?.color || 'darkblue'"
                                        @click="handleClose"
                                        >{{ cancelText || t('pt.dialog.cancel') }}</pt-button
                                    >
                                    <pt-button
                                        v-show="showConfirmButton"
                                        v-bind="confirmButtonInfo"
                                        :color="confirmButtonInfo?.color || 'green'"
                                        class="pt-dialog__footer__button__confirm"
                                        @click="handleConfirm"
                                        >{{ confirmText || t('pt.dialog.confirm') }}</pt-button
                                    >
                                </div>
                            </slot>
                        </template>
                    </div>
                </div>
            </pt-overlay>
        </transition>
    </teleport>
</template>
<script lang="ts">
import { defineComponent, ref } from 'vue';
import { TrapFocus } from '../../directives';
import { isValidWidthUnit } from '../../../utils/validators';
import { Overlay } from '../../overlay';
import { t } from '../../locale';
import {
    default as useDialog,
    CLOSE_EVENT,
    CLOSED_EVENT,
    OPEN_EVENT,
    OPENED_EVENT,
    CONFIRM_EVENT,
    UPDATE_MODEL_EVENT,
} from './useDialog';

import type { PropType, SetupContext } from 'vue';
import type { ButtonProps, SizeType } from './dialog';
export default defineComponent({
    name: 'PtDialogV2',
    components: {
        'pt-overlay': Overlay,
    },
    directives: {
        TrapFocus,
    },
    props: {
        appendToBody: {
            type: Boolean,
            default: false,
        },
        beforeClose: {
            type: Function as PropType<(...args: any[]) => unknown>,
        },
        destroyOnClose: {
            type: Boolean,
            default: false,
        },
        center: {
            type: Boolean,
            default: false,
        },
        customClass: {
            type: String,
            default: '',
        },
        closeOnClickModal: {
            type: Boolean,
            default: false,
        },
        closeOnPressEscape: {
            type: Boolean,
            default: false,
        },
        fullscreen: {
            type: Boolean,
            default: false,
        },
        lockScroll: {
            type: Boolean,
            default: true,
        },
        modal: {
            type: Boolean,
            default: true,
        },
        showClose: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        openDelay: {
            type: Number,
            default: 0,
        },
        closeDelay: {
            type: Number,
            default: 0,
        },
        modelValue: {
            type: Boolean,
            required: true,
        },
        modalClass: String,
        width: {
            type: String,
            validator: isValidWidthUnit,
        },
        size: {
            type: String as PropType<SizeType>,
            default: 'medium',
        },
        hasScroll: {
            type: Boolean,
            default: true,
        },
        iconKey: {
            type: String,
        },
        cancelButtonInfo: {
            type: Object as PropType<ButtonProps>,
        },
        cancelText: {
            type: String,
        },
        showCancelButton: {
            type: Boolean,
            default: true,
        },
        confirmButtonInfo: {
            type: Object as PropType<ButtonProps>,
        },
        confirmText: {
            type: String,
        },
        showConfirmButton: {
            type: Boolean,
            default: true,
        },
        hasFooter: {
            type: Boolean,
            default: true,
        },
        zIndex: {
            type: Number,
        },
    },
    emits: [OPEN_EVENT, OPENED_EVENT, CLOSE_EVENT, CLOSED_EVENT, UPDATE_MODEL_EVENT, CONFIRM_EVENT],
    setup(props, ctx) {
        const dialogRef = ref<HTMLElement>(null);
        return {
            ...useDialog(props, ctx as SetupContext, dialogRef),
            dialogRef,
            t,
        };
    },
});
</script>

<style lang="scss" src="../style/dialog.scss"></style>
